import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_LABELS = {
    deferral_overdue: 'Diferido vencido', aog: 'Aeronave en AOG', aog_repaired: 'AOG liberada', aog_reclassified_admin: 'AOG reclasificada',
    equipment_change_unrated: 'Cambio de equipo sin habilitación', irops: 'IROPS', captain_report: 'Reporte del capitán',
    report_update: 'Reporte actualizado', fdtl_alert: 'Alerta FDTL',
};
const SEVERITY = { danger: 'border-l-red-500', warning: 'border-l-amber-500', info: 'border-l-sky-500' };

export default function NotificationsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [onlyUnread, setOnlyUnread] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/notifications`, { params: { exclude_type: 'op_message', unread_only: onlyUnread, limit: 200 } });
            setItems(r.data);
        } catch { toast.error('No se pudieron cargar los avisos'); }
        finally { setLoading(false); }
    }, [onlyUnread]);
    useEffect(() => { load(); }, [load]);

    const markRead = async (id) => {
        await axios.patch(`${API}/notifications/${id}/read`).catch(() => {});
        setItems(xs => xs.map(x => x.id === id ? { ...x, read: true } : x));
        window.dispatchEvent(new Event('notifications-changed'));
    };
    const markAll = async () => {
        await axios.patch(`${API}/notifications/read-all`, null, { params: { exclude_type: 'op_message' } }).catch(() => toast.error('No se pudo marcar'));
        window.dispatchEvent(new Event('notifications-changed'));
        load();
    };
    const unread = items.filter(i => !i.read).length;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed'] flex items-center gap-2"><Bell className="w-7 h-7 text-primary" />Notificaciones</h1>
                    <p className="text-sm text-muted-foreground">Avisos automáticos del sistema para ti: diferidos vencidos, aeronaves en AOG, cambios de equipo autorizados e irregularidades. Los mensajes del equipo están en Mensajes Operacionales.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setOnlyUnread(v => !v)} data-testid="notifications-filter-unread">{onlyUnread ? 'Ver todas' : 'Solo no leídas'}</Button>
                    <Button size="sm" onClick={markAll} disabled={unread === 0} data-testid="notifications-mark-all"><CheckCheck className="w-4 h-4 mr-1" />Marcar todas como leídas</Button>
                </div>
            </div>
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : items.length === 0 ? (
                <Card className="card-aviation"><CardContent className="p-10 text-center text-muted-foreground" data-testid="notifications-empty">No tienes avisos {onlyUnread ? 'sin leer' : 'todavía'}.</CardContent></Card>
            ) : (
                <div className="space-y-2" data-testid="notifications-list">
                    {items.map(n => (
                        <div key={n.id} className={`rounded-lg border border-border/60 border-l-4 ${SEVERITY[n.severity] || SEVERITY.info} p-3 flex items-start justify-between gap-3 ${n.read ? 'opacity-70' : 'bg-muted/20'}`} data-testid={`notifications-item-${n.id}`}>
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[n.type] || 'Aviso'}</Badge>
                                    <p className="text-sm font-semibold">{n.title}</p>
                                    {!n.read && <span className="text-[10px] font-bold uppercase text-sky-400" data-testid={`notifications-unread-${n.id}`}>No leído</span>}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">{n.message}</p>
                                <p className="text-[11px] text-muted-foreground/70 mt-1 font-mono">{new Date(n.created_at).toLocaleString('es')}</p>
                            </div>
                            {!n.read && <Button variant="ghost" size="sm" onClick={() => markRead(n.id)} data-testid={`notifications-read-${n.id}`}>Marcar leído</Button>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
